<script setup>
import {ref, computed, watch} from 'vue'
import axios from 'axios'
import PromoCodeSection from './PromoCodeSection.vue'
import PointsSection from './PointsSection.vue'
import GiftCardSection from "./GiftCardSection.vue";
import {useConfigurateurStore} from "../../../stores/configurateur";

const configurateurStore = useConfigurateurStore();

const cart = ref(null)
const options = ref([])
const optionReviewMessage = ref('')
const payments = ref([])
const beneficiaires = ref({})
const beneficiaireErrors = ref({})
const existingBeneficiaires = ref([])
const selectedBeneficiaire = ref({})
const optionChoices = ref([])
const selectedPaymentMethod = ref('')
const showBeneficiaireForms = ref({})
const showAddressForm = ref(false)
const termsAccepted = ref(false)
const giftCardEmailOptions = ref({})
const giftCardEmailErrors = ref({})
const countries = ref([])
const states = ref([])
const customerAddresses = ref([])
const selectedAddressId = ref(null)
const shippings = ref([])
const customerAdresses = ref([])
const promoCodeDiscount = ref('0.00')
const pointsDiscount = ref('0.00')
const user = ref(null) // À remplacer par votre logique d'authentification réelle
const giftCardDiscount = ref('0.00')

// États pour tracker la sauvegarde des bénéficiaires
const beneficiaireSaved = ref({}) // { [itemId]: true|false } - indique si le bénéficiaire est sauvegardé
const savingBeneficiaire = ref({}) // { [itemId]: true|false } - indique si une sauvegarde est en cours

// Système de notifications
const notification = ref({
    show: false,
    message: '',
    type: 'success', // success, error, info, warning
    timeout: null
})

// Fonction pour basculer la sélection d'une option
const toggleOption = (productId) => {
    const index = optionChoices.value.indexOf(productId)
    if (index === -1) {
        optionChoices.value.push(productId)
    } else {
        optionChoices.value.splice(index, 1)
    }
}

// Fonction pour ajouter les options sélectionnées au panier
const addSelectedOptionsToCart = async () => {
    if (optionChoices.value.length === 0) {
        showNotification('Veuillez sélectionner au moins une option', 'warning')
        return
    }

    try {
        showNotification('Ajout des options au panier...', 'info')

        // Préparer les données des options pour l'API
        const optionsData = optionChoices.value.map(offerKey => {
            const option = options.value.find(opt => opt.offer_key === offerKey)
            return {
                product_id: option.product_id,
                target_item_id: option.target_item_id,
                quantity: 1,
                option_reference_id: option.option_reference_id,
                reference_id: option.option_reference_id,
                option_id: option.id,
                code: option.code,
                montant_ttc: option.montant_ttc,
            }
        })

        // Appeler l'API pour ajouter les options
        const response = await axios.post('/api/checkout/cart', {
            options: optionsData
        })

        if (response.data && response.data.data) {
            // Mettre à jour le panier avec les données retournées
            cart.value = response.data.data

            // Vider les options sélectionnées
            optionChoices.value = []

            // Recharger la page pour récupérer les nouvelles options disponibles
            window.location.reload()
        }
    } catch (error) {
        console.error('Erreur lors de l\'ajout des options:', error)

        if (error.response && error.response.data && error.response.data.message) {
            showNotification(error.response.data.message, 'error')
        } else {
            showNotification('Erreur lors de l\'ajout des options au panier', 'error')
        }
    }
}

// Fonction pour mettre à jour le montant de la réduction du code promo
const updatePromoCodeDiscount = (discount) => {
    promoCodeDiscount.value = discount
}

// Fonction pour mettre à jour le montant de la réduction des points
const updatePointsDiscount = (discount) => {
    pointsDiscount.value = discount

}

const updateGiftCardDiscount = (discount) => {
    giftCardDiscount.value = discount
}

// Adresse
const address = ref({
    company_name: '',
    first_name: '',
    last_name: '',
    email: '',
    address: [],
    country: '',
    state: '',
    city: '',
    postcode: '',
    phone: ''
})
const addressErrors = ref({})

// Récupérer les pays
const fetchCountries = async () => {
    try {
        const response = await axios.get('/api/core/countries')
        countries.value = response.data.data
    } catch (error) {
        console.error('Erreur lors de la récupération des pays:', error)
    }
}

// Récupérer les régions pour un pays spécifique
const fetchStates = async (countryCode) => {
    if (!countryCode) return

    try {
        const response = await axios.get(`/api/core/states`)
        states.value = response.data.data[countryCode]
    } catch (error) {
        console.error('Erreur lors de la récupération des régions:', error)
    }
}

const fetchAddresses = async () => {
    try {
        const response = await axios.get(`/api/customer/addresses`)
        customerAddresses.value = response.data.data || []
    } catch (error) {
        console.error('Erreur lors de la récupération des régions:', error)
    }
}

const fetchBeneficiaires = async () => {
    if (!user.value) return
    try {
        const response = await axios.get('/api/beneficiaires')
        existingBeneficiaires.value = response.data.data || []
    } catch (error) {
        console.error('Erreur lors de la récupération des bénéficiaires:', error)
    }
}

const formatPrice = (price) => {
    const amount = Number(price)
    return Number.isFinite(amount) ? amount.toLocaleString('fr-FR', { maximumFractionDigits: 2 }) : ''
}

// Appel des fonctions d'initialisation
// Les adresses privées sont chargées après identification du client.

// Appeler la fonction pour récupérer les pays au chargement du composant
fetchCountries()

// Observer les changements de pays pour charger les régions correspondantes
watch(() => address.value.country, (newCountryCode) => {
    if (newCountryCode) {
        fetchStates(newCountryCode)
    } else {
        states.value = []
    }
})

const response = await axios.get('/api/checkout/cart')
cart.value = response.data.data
options.value = response.data.options
optionReviewMessage.value = response.data.message ?? ''
payments.value = response.data.payments
user.value = response.data.user
if (user.value) await fetchAddresses()
existingBeneficiaires.value = response.data.beneficiaires

// Si le client a des adresses, présélectionner la première
if (customerAddresses.value.length > 0) {
    selectedAddressId.value = customerAddresses.value[0].id
}
shippings.value = response.data.shippings
customerAdresses.value = response.data.adresses

// Sélectionner automatiquement la première méthode de paiement si disponible
if (payments.value && payments.value.length > 0) {
    selectedPaymentMethod.value = payments.value[0]
}

// Initialiser les bénéficiaires pour chaque article
if (cart.value && cart.value.items) {
    cart.value.items.forEach(item => {
        beneficiaires.value[item.id] = item.beneficiaire ? item.beneficiaire : {
            id: null,
            gender: 'male',
            last_name: '',
            first_name: '',
            email: '',
            address: '',
            address_complement: '',
            postcode: '',
            city: ''
        }
        beneficiaireErrors.value[item.id] = {}
        showBeneficiaireForms.value[item.id] = item.beneficiaire ? true : false
        selectedBeneficiaire.value[item.id] = item.beneficiaire ? item.beneficiaire.id : null

        // Initialiser l'état de sauvegarde
        beneficiaireSaved.value[item.id] = item.beneficiaire ? true : false
        savingBeneficiaire.value[item.id] = false

        // Initialiser les options d'envoi d'email pour les cartes cadeaux
        if (item.type === 'gift_card') {
            giftCardEmailOptions.value[item.id] = {
                sendTo: item.additional?.gift_card_email?.sendTo || 'buyer', // 'buyer' ou 'beneficiary'
                message: item.additional?.gift_card_email?.message || ''
            }
            giftCardEmailErrors.value[item.id] = {}
        }
    })
}


const refreshBeneficiaryOptions = async () => {
    const response = await axios.get('/api/checkout/cart')
    cart.value = response.data.data
    options.value = response.data.options ?? []
    optionChoices.value = []
    // Préserver les formulaires des autres bénéficiaires en cours de saisie.
}

// Fonction pour basculer l'affichage du formulaire du bénéficiaire
const toggleBeneficiaireForm = async (itemId) => {
    // Si l'utilisateur devient le bénéficiaire, on réinitialise les champs avec ses infos
    if (!showBeneficiaireForms.value[itemId]) {
        try {
            savingBeneficiaire.value[itemId] = true
            let response = await axios.put(`/api/checkout/cart-item/${itemId}/beneficiaire`, {
                beneficiaire: null
            })
            selectedBeneficiaire.value[itemId] = null;
            await refreshBeneficiaryOptions();
            beneficiaireSaved.value[itemId] = true; // L'utilisateur est automatiquement "sauvegardé"

            // CRUCIAL: Mettre à jour les données du panier côté client
            const itemIndex = cart.value.items.findIndex(item => item.id === itemId)
            if (itemIndex !== -1 && cart.value.items[itemIndex].additional) {
                delete cart.value.items[itemIndex].additional.beneficiaire
            }

            showNotification('Vous êtes maintenant le bénéficiaire', 'success')
        } catch (error) {
            console.error('Erreur lors de la mise à jour du bénéficiaire:', error)
            showNotification('Erreur lors de la mise à jour', 'error')
            // Remettre le switch dans l'état précédent en cas d'erreur
            showBeneficiaireForms.value[itemId] = true
        } finally {
            savingBeneficiaire.value[itemId] = false
        }
    } else {
        // Si l'utilisateur n'est pas le bénéficiaire, on peut vider les champs
        beneficiaires.value[itemId] = {
            id: null,
            gender: 'male',
            last_name: '',
            first_name: '',
            email: '',
            address: '',
            address_complement: '',
            postcode: '',
            city: ''
        }
        beneficiaireSaved.value[itemId] = false; // Nouveau bénéficiaire non sauvegardé
        // Charger les bénéficiaires existants si ce n'est pas déjà fait
        if (existingBeneficiaires.value.length === 0) {
            fetchBeneficiaires()
        }
    }
    // Réinitialiser les erreurs quand on change le bénéficiaire
    beneficiaireErrors.value[itemId] = {}

    // Réinitialiser aussi les erreurs d'email pour les cartes cadeaux
    if (giftCardEmailErrors.value[itemId]) {
        giftCardEmailErrors.value[itemId] = {}
    }
}

// Fonction pour sélectionner un bénéficiaire existant
const selectBeneficiaire = async (beneficiaire, itemId) => {
    beneficiaires.value[itemId] = {
        id: beneficiaire.id,
        gender: beneficiaire.gender || 'male',
        last_name: beneficiaire.last_name,
        first_name: beneficiaire.first_name,
        email: beneficiaire.email,
        address: beneficiaire.address || '',
        address_complement: beneficiaire.address_complement || '',
        postcode: beneficiaire.postcode || '',
        city: beneficiaire.city || ''
    }
    selectedBeneficiaire.value[itemId] = beneficiaire.id

    // Sauvegarder immédiatement le bénéficiaire sélectionné
    try {
        savingBeneficiaire.value[itemId] = true
        const response = await axios.put(`/api/checkout/cart-item/${itemId}/beneficiaire`, {
            beneficiaire: beneficiaires.value[itemId]
        })

        if (response.data.id) {
            beneficiaires.value[itemId].id = response.data.id
        }

        // CRUCIAL: Mettre à jour les données du panier côté client
        const itemIndex = cart.value.items.findIndex(item => item.id === itemId)
        if (itemIndex !== -1) {
            if (!cart.value.items[itemIndex].additional) {
                cart.value.items[itemIndex].additional = {}
            }
            cart.value.items[itemIndex].additional.beneficiaire = beneficiaires.value[itemId]
        }

        await refreshBeneficiaryOptions()
        beneficiaireSaved.value[itemId] = true
        showNotification(response.data.message || 'Bénéficiaire associé avec succès', 'success')
    } catch (error) {
        console.error('Erreur lors de l\'association du bénéficiaire:', error)
        beneficiaireSaved.value[itemId] = false
        showNotification('Erreur lors de l\'association du bénéficiaire', 'error')
    } finally {
        savingBeneficiaire.value[itemId] = false
    }
}

// Fonction pour afficher une notification
const showNotification = (message, type = 'success', duration = 3000) => {
    // Si une notification est déjà affichée, la supprimer
    if (notification.value.timeout) {
        clearTimeout(notification.value.timeout)
    }

    // Configurer la nouvelle notification
    notification.value = {
        show: true,
        message,
        type,
        timeout: setTimeout(() => {
            notification.value.show = false
        }, duration)
    }
}

// Fonction pour mettre à jour le nombre de tours
const updateTours = async (itemId, newQuantity) => {
    // Vérifier que la quantité est au moins 1
    if (newQuantity < 1) return;

    try {
        // Préparer les données dans le format attendu par l'API
        // L'API attend un objet avec une propriété "qty" qui est un dictionnaire avec les ID d'articles comme clés
        const requestData = {
            qty: {
                [itemId]: newQuantity
            }
        };

        // Mettre à jour la quantité dans l'API
        const response = await axios.put(`/api/checkout/cart`, requestData);

        // Si la mise à jour réussit, mettre à jour l'interface utilisateur
        if (response.data && response.data.data) {
            // Mettre à jour l'article localement
            cart.value.items.forEach(item => {
                if (item.id === itemId) {
                    item.quantity = newQuantity;
                    // Le prix total sera mis à jour automatiquement par l'API
                }
            });

            // Mettre à jour le panier avec les données retournées par l'API
            cart.value = response.data.data;

            // Afficher un message de succès
            if (response.data.message) {
                showNotification(response.data.message || 'Nombre de tours mis à jour avec succès', 'success');
            } else {
                showNotification('Nombre de tours mis à jour avec succès', 'success');
            }
        }
    } catch (error) {
        console.error('Erreur lors de la mise à jour du nombre de tours:', error);

        // Gérer les messages d'erreur éventuels de l'API
        if (error.response && error.response.data && error.response.data.message) {
            showNotification(error.response.data.message, 'error');
        } else {
            showNotification('Erreur lors de la mise à jour du nombre de tours', 'error');
        }
    }
}

// Fonction pour supprimer un article du panier
const removeCartItem = async (itemId) => {
    try {
        showNotification('Suppression de l\'article en cours...', 'info');

        // Appel à l'API pour supprimer l'article
        const response = await axios.delete(`/api/checkout/cart-item/${itemId}`);

        // Si la suppression est réussie, mettre à jour l'interface utilisateur
        if (response.data && response.data.success) {
            // data est null quand le panier devient vide (supprimé côté back)
            cart.value = response.data.data ?? null;
            options.value = response.data.options ?? [];

            // Panier vide : purger la configuration persistée pour éviter
            // la popup "reprendre la configuration existante" au prochain clic
            if (!cart.value || !cart.value.items || cart.value.items.length === 0) {
                configurateurStore.cleanConfigurateur();
            }

            // Rafraîchir le compteur de panier dans le header
            if (typeof window.refreshCartCount === 'function') {
                window.refreshCartCount();
            }

            // Afficher un message de succès
            showNotification(response.data.message || 'Article supprimé avec succès', 'success');
        }
    } catch (error) {
        console.error('Erreur lors de la suppression de l\'article:', error);

        // Gérer les messages d'erreur éventuels de l'API
        if (error.response && error.response.data && error.response.data.message) {
            showNotification(error.response.data.message, 'error');
        } else {
            showNotification('Erreur lors de la suppression de l\'article', 'error');
        }
    }
}

const addBeneficiaire = async (itemId) => {
    beneficiaireErrors.value[itemId] = {}

    // Validation simple des champs
    if (!beneficiaires.value[itemId].last_name) {
        beneficiaireErrors.value[itemId].last_name = 'Le nom est requis'
    }
    if (!beneficiaires.value[itemId].first_name) {
        beneficiaireErrors.value[itemId].first_name = 'Le prénom est requis'
    }
    if (beneficiaires.value[itemId].email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(beneficiaires.value[itemId].email)) {
        beneficiaireErrors.value[itemId].email = 'Veuillez entrer un email valide'
    }

    // Validation des champs d'adresse (obligatoires)
    if (!beneficiaires.value[itemId].address) {
        beneficiaireErrors.value[itemId].address = 'L\'adresse est requise'
    }
    if (!beneficiaires.value[itemId].postcode) {
        beneficiaireErrors.value[itemId].postcode = 'Le code postal est requis'
    } else if (!/^\d{5}$/.test(beneficiaires.value[itemId].postcode)) {
        beneficiaireErrors.value[itemId].postcode = 'Le code postal doit contenir 5 chiffres'
    }
    if (!beneficiaires.value[itemId].city) {
        beneficiaireErrors.value[itemId].city = 'La ville est requise'
    }

    // Si pas d'erreurs, nous considérons que le bénéficiaire est ajouté
    if (Object.keys(beneficiaireErrors.value[itemId]).length === 0) {
        try {
            savingBeneficiaire.value[itemId] = true
            let response = await axios.put(`/api/checkout/cart-item/${itemId}/beneficiaire`, {
                beneficiaire: beneficiaires.value[itemId]
            })
            beneficiaires.value[itemId].id = response.data.id

            // CRUCIAL: Mettre à jour les données du panier côté client
            const itemIndex = cart.value.items.findIndex(item => item.id === itemId)
            if (itemIndex !== -1) {
                if (!cart.value.items[itemIndex].additional) {
                    cart.value.items[itemIndex].additional = {}
                }
                cart.value.items[itemIndex].additional.beneficiaire = beneficiaires.value[itemId]
            }

            await refreshBeneficiaryOptions()
            beneficiaireSaved.value[itemId] = true
            showNotification(response.data.message, 'success')
        } catch (error) {
            console.error('Erreur lors de l\'enregistrement du bénéficiaire:', error)
            beneficiaireSaved.value[itemId] = false
            showNotification(error.response?.data?.message || 'Erreur lors de l\'enregistrement du bénéficiaire', 'error')
        } finally {
            savingBeneficiaire.value[itemId] = false
        }
    }
}

// Fonction pour valider et soumettre le formulaire d'adresse
const saveAddress = async () => {
    // Réinitialiser les erreurs
    addressErrors.value = {}

    // Valider le formulaire
    if (!address.value.first_name) {
        addressErrors.value.first_name = 'Le prénom est requis'
    }

    if (!address.value.last_name) {
        addressErrors.value.last_name = 'Le nom est requis'
    }

    if (!address.value.email) {
        addressErrors.value.email = 'L\'email est requis'
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(address.value.email)) {
        addressErrors.value.email = 'Veuillez entrer un email valide'
    }

    if (!address.value.address) {
        addressErrors.value.address = 'L\'adresse est requise'
    }

    if (!address.value.country) {
        addressErrors.value.country = 'Le pays est requis'
    }

    if (!address.value.city) {
        addressErrors.value.city = 'La ville est requise'
    }

    if (!address.value.postcode) {
        addressErrors.value.postcode = 'Le code postal est requis'
    }

    if (!address.value.phone) {
        addressErrors.value.phone = 'Le numéro de téléphone est requis'
    } else if (!/^\d{10}$/.test(address.value.phone.replace(/\s/g, ''))) {
        addressErrors.value.phone = 'Veuillez entrer un numéro de téléphone valide'
    }

    // Si pas d'erreurs, soumettre le formulaire
    if (Object.keys(addressErrors.value).length === 0) {
        try {
            const response = await axios.post('/api/checkout/onepage/addresses', {
                billing: {
                    ...address.value,
                    address_id: undefined,
                    use_for_shipping: true,
                },
                shipping: {
                    ...address.value,
                    address_id: undefined
                }
            })

            if (response.data) {
                showAddressForm.value = false
                if (response.data.redirect) {
                    window.location.href = response.data.data
                } else {
                    fetchAddresses();
                    selectedAddressId.value = response.data.data.id
                    showNotification('Adresse enregistrée avec succès!', 'success')
                }
            }
        } catch (error) {
            console.error('Erreur lors de l\'enregistrement de l\'adresse:', error)
            showNotification('Une erreur est survenue lors de l\'enregistrement de l\'adresse.', 'error')
        }
    } else {
        showNotification('Veuillez corriger les erreurs dans le formulaire', 'warning')
    }
}

const toggleAddressForm = () => {
    showAddressForm.value = !showAddressForm.value

    // Si on ferme le formulaire d'ajout, on réinitialise les erreurs
    if (!showAddressForm.value) {
        addressErrors.value = {}
    }
}

// Sélectionner une adresse existante
const selectAddress = (addressId) => {
    selectedAddressId.value = addressId

    // Trouver l'adresse sélectionnée
    const selectedAddress = customerAddresses.value.find(addr => addr.id === addressId)

    if (selectedAddress) {
        // Préremplir le formulaire avec l'adresse sélectionnée si l'utilisateur veut la modifier plus tard
        address.value = {
            company_name: selectedAddress.company_name || '',
            first_name: selectedAddress.first_name,
            last_name: selectedAddress.last_name,
            email: selectedAddress.email,
            address: Array.isArray(selectedAddress.address) ? selectedAddress.address[0] : selectedAddress.address,
            country: selectedAddress.country,
            state: selectedAddress.state,
            city: selectedAddress.city,
            postcode: selectedAddress.postcode,
            phone: selectedAddress.phone
        }

        // Charger les états pour le pays sélectionné
        if (selectedAddress.country) {
            fetchStates(selectedAddress.country)
        }
    }
}

// Utiliser l'adresse sélectionnée pour la commande
const useSelectedAddress = async () => {
    if (!selectedAddressId.value) {
        showNotification('Veuillez sélectionner une adresse', 'warning')
        return
    }

    try {
        const selectedAddress = customerAddresses.value.find(addr => addr.id === selectedAddressId.value)

        if (selectedAddress) {
            const response = await axios.post('/api/checkout/cart/address-choice', {
                address_id: selectedAddressId.value
            });
            // const response = await axios.post('/api/checkout/onepage/addresses', {
            //     billing: {
            //         ...selectedAddress,
            //         address_id: selectedAddressId.value,
            //         use_for_shipping: true,
            //     },
            //     shipping: {
            //         ...selectedAddress,
            //         address_id: selectedAddressId.value
            //     }
            // })

            if (response.data) {
                showNotification('Adresse sélectionnée avec succès!', 'success')
            }
        }
    } catch (error) {
        console.error('Erreur lors de la sélection de l\'adresse:', error)
        showNotification(error.response?.data?.message || 'Une erreur est survenue lors de la sélection de l\'adresse.', 'error')
    }
}

// Fonction pour valider les préférences d'email des cartes cadeaux
const validateGiftCardEmailOptions = () => {
    let isValid = true
    giftCardEmailErrors.value = {}

    // Vérifier chaque carte cadeau
    if (cart.value && cart.value.items) {
        cart.value.items.forEach(item => {
            // Valider uniquement si c'est une carte cadeau ET que le formulaire bénéficiaire est affiché
            if (item.type === 'gift_card' && showBeneficiaireForms.value[item.id]) {
                giftCardEmailErrors.value[item.id] = {}

                // Vérifier que sendTo est défini
                if (!giftCardEmailOptions.value[item.id]?.sendTo) {
                    giftCardEmailErrors.value[item.id].sendTo = 'Veuillez choisir à qui envoyer le bon cadeau'
                    isValid = false
                }

                // Vérifier la longueur du message si présent
                if (giftCardEmailOptions.value[item.id]?.message &&
                    giftCardEmailOptions.value[item.id].message.length > 40) {
                    giftCardEmailErrors.value[item.id].message = 'Le message ne peut pas dépasser 40 caractères'
                    isValid = false
                }
            }
        })
    }

    return isValid
}

// Fonction pour sauvegarder les préférences d'email dans le panier avant validation
const saveGiftCardEmailOptionsToCart = async () => {
    if (!cart.value || !cart.value.items) return

    try {
        for (const item of cart.value.items) {
            // Sauvegarder pour toutes les cartes cadeaux qui ont des préférences d'email définies
            if (item.type === 'gift_card' && giftCardEmailOptions.value[item.id]) {
                // Si le formulaire bénéficiaire n'est pas affiché mais qu'il y a des données d'email,
                // cela signifie que l'utilisateur avait précédemment défini un bénéficiaire puis a décoché
                const shouldSave = showBeneficiaireForms.value[item.id] || giftCardEmailOptions.value[item.id].sendTo

                if (shouldSave) {
                    // Mise à jour silencieuse des informations dans l'item
                    await axios.put(`/api/checkout/cart-item/${item.id}/beneficiaire`, {
                        beneficiaire: item.additional?.beneficiaire || null,
                        gift_card_email: giftCardEmailOptions.value[item.id]
                    })
                }
            }
        }
    } catch (error) {
        console.error('Erreur lors de la sauvegarde des préférences email:', error)
    }
}

const handleSubmitCart = async () => {
    try {
        // Vérifier que tous les bénéficiaires sont sauvegardés
        const unsavedBeneficiaires = []
        if (cart.value && cart.value.items) {
            cart.value.items.forEach(item => {
                if ((item.type === 'stage' || item.type === 'gift_card') &&
                    showBeneficiaireForms.value[item.id] &&
                    !beneficiaireSaved.value[item.id]) {
                    unsavedBeneficiaires.push(item.name)
                }
            })
        }

        if (unsavedBeneficiaires.length > 0) {
            const itemNames = unsavedBeneficiaires.join(', ')
            showNotification(`Veuillez enregistrer les bénéficiaires pour : ${itemNames}`, 'warning')

            // Scroll vers le premier élément non sauvegardé
            const firstUnsavedItem = cart.value.items.find(item =>
                (item.type === 'stage' || item.type === 'gift_card') &&
                showBeneficiaireForms.value[item.id] &&
                !beneficiaireSaved.value[item.id]
            )
            if (firstUnsavedItem) {
                const element = document.querySelector(`[data-item-id="${firstUnsavedItem.id}"]`)
                if (element) {
                    element.scrollIntoView({behavior: 'smooth', block: 'center'})
                }
            }
            return
        }

        // Vérification d'acceptation des CGV
        if (!termsAccepted.value) {
            showNotification('Vous devez accepter les conditions générales de vente pour continuer', 'warning')
            return
        }

        // Vérification d'une méthode de paiement sélectionnée
        if (!selectedPaymentMethod.value) {
            showNotification('Veuillez sélectionner une méthode de paiement', 'warning')
            return
        }

        // Vérification d'une adresse sélectionnée
        if (!selectedAddressId.value) {
            showNotification('Veuillez sélectionner ou ajouter une adresse', 'warning')
            return
        }

        // Validation des préférences d'email pour les cartes cadeaux
        if (!validateGiftCardEmailOptions()) {
            showNotification('Veuillez compléter les informations d\'envoi pour les cartes cadeaux', 'warning')
            return
        }

        showNotification('Traitement de votre commande en cours...', 'info')

        // Sauvegarder les préférences d'email des cartes cadeaux
        await saveGiftCardEmailOptionsToCart()

        let payment = selectedPaymentMethod.value;

        if (cart.value.grand_total == 0) {
            payment = payments.value.find(p => p.method === 'moneytransfer')
        }

        if (selectedAddressId.value) {
            const response = await axios.post('/api/checkout/cart/address-choice', {
                address_id: selectedAddressId.value
            });
        }
        const response = await axios.post('/api/checkout/onepage/payment-methods', {payment})
        const responseShippingMethod = await axios.post('/api/checkout/onepage/shipping-methods', {shipping_method: shippings.value[1].method})
        const responseOrder = await axios.post('/api/checkout/onepage/orders')

        // Commande créée : le panier est vidé côté back, on purge aussi la
        // configuration persistée côté client pour éviter la popup de reprise
        configurateurStore.cleanConfigurateur();

        if (responseOrder.data.data.redirect) {
            showNotification('Redirection vers la page de paiement...', 'success')
            setTimeout(() => {
                window.location.href = responseOrder.data.data.redirect_url
            }, 1000)
        } else {
            showNotification('Commande créée avec succès!', 'success')
        }
    } catch (error) {
        console.error('Erreur lors de la création de la commande:', error)

        if (error.response && error.response.data && error.response.data.message) {
            showNotification(error.response.data.message, 'error')
        } else {
            showNotification('Une erreur est survenue lors de la création de votre commande', 'error')
        }
    }
}

const pointUpdateCart = (cartUpdated) => {
    cart.value = {
        ...cart.value,
        ...cartUpdated,
    };
}

</script>

<template>
    <div class="container mt-5 mb-5">
        <!-- Composant de notification -->
        <transition name="fade">
            <div v-if="notification.show" class="notification-toast" :class="'notification-' + notification.type">
                <div class="notification-icon">
                    <i class="bi" :class="{
                        'bi-check-circle-fill': notification.type === 'success',
                        'bi-exclamation-circle-fill': notification.type === 'error',
                        'bi-info-circle-fill': notification.type === 'info',
                        'bi-exclamation-triangle-fill': notification.type === 'warning'
                    }"></i>
                </div>
                <div class="notification-content">
                    {{ notification.message }}
                </div>
                <button class="notification-close" @click="notification.show = false">
                    <i class="bi bi-x"></i>
                </button>
            </div>
        </transition>

        <!-- En-tête panier -->
        <div class="cart-page-header mb-4">
            <div class="card shadow-sm border-0">
                <div class="card-header bg-dark text-white d-flex align-items-center justify-content-between">
                    <h1 class="h4 mb-0 text-white"><i class="bi bi-cart4 me-2"></i>Votre panier</h1>
                    <span v-if="cart && cart.items && cart.items.length" class="badge bg-light text-dark fs-6">{{ cart.items.length }} article(s)</span>
                </div>
            </div>
        </div>

        <div class="row g-4">
            <!-- Colonne principale -->
            <div class="col-lg-8">
                <div v-if="cart && cart.items && cart.items.length > 0" class="card shadow-sm border-0 mb-4">
                    <div class="card-header bg-white border-bottom">
                        <h3 class="h5 mb-0"><i class="bi bi-bag me-2"></i>Articles</h3>
                    </div>
                    <div class="card-body p-0">
                    <div v-for="item in cart.items" :key="item.id" class="cart-item p-4" :data-item-id="item.id">
                        <div class="row align-items-center">
                            <!-- Image produit -->
                            <div class="col-md-2 col-3">
                                <img v-if="item.infos && item.infos.voiture && item.infos.voiture.image"
                                     :src="item.infos.voiture.image" class="img-fluid rounded"
                                     :alt="item.name" style="max-height: 80px; object-fit: cover;">
                                <img v-else-if="item.infos && item.infos.option && item.infos.option.image"
                                     :src="item.infos.option.image" class="img-fluid rounded"
                                     :alt="item.name" style="max-height: 80px; object-fit: cover;">
                                <img v-else-if="item.infos && item.infos.circuit && item.infos.circuit.image"
                                     :src="item.infos.circuit.image" class="img-fluid rounded"
                                     :alt="item.name" style="max-height: 80px; object-fit: cover;">
                                <img v-else-if="item.infos?.gift_card?.image"
                                     :src="item.infos.gift_card.image" class="img-fluid rounded"
                                     :alt="item.name" style="max-height: 80px; object-fit: contain;">
                                <div v-else class="bg-light rounded d-flex align-items-center justify-content-center"
                                     style="height: 80px;">
                                    <i class="bi bi-image text-muted fs-2"></i>
                                </div>
                            </div>

                            <!-- Détails produit -->
                            <div class="col-md-6">
                                <h6 class="mb-1">
                                    {{ item.name }}
                                    <span v-if="item.type === 'stage'" class="badge bg-primary">Stage</span>
                                    <span v-else-if="item.type === 'gift_card'" class="badge bg-success">Bon cadeau</span>
                                </h6>
                                <div v-if="item.infos && item.infos.date" class="text-muted small">
                                    <i class="bi bi-calendar-event me-1"></i>{{ item.infos.date }} · {{ item.infos.creneau }}
                                </div>
                                <div v-if="item.infos && item.infos.voiture" class="text-muted small">
                                    <i class="bi bi-car-front-fill me-1"></i>{{ item.infos.voiture.marque }} - {{ item.infos.voiture.designation }}
                                </div>
                                <div v-if="item.infos && item.infos.circuit" class="text-muted small">
                                    <i class="bi bi-geo-alt me-1"></i>{{ item.infos.circuit.designation }}
                                </div>
                                <div v-if="item.infos && item.infos.option" class="text-muted small">
                                    <i class="bi bi-plus-circle me-1"></i>{{ item.infos.option.designation || 'Option supplémentaire' }}
                                </div>
                            </div>

                            <!-- Quantité et prix -->
                            <div class="col-md-4 text-md-end">
                                <div class="mb-1">
                                    <span class="text-muted">{{ item.type === 'stage' ? 'Nombre de tours : ' : 'Quantité : ' }}</span>
                                    <strong>{{ item.infos?.nb_tours ?? item.quantity }}</strong>
                                </div>
                                <div class="mb-1 d-none" aria-hidden="true">
                                    <span class="text-muted">Prix unitaire : </span>
                                    <strong>{{ item.formatted_price_incl_tax }}</strong>
                                </div>
                                <div class="fs-5 fw-bold text-primary">
                                    <span v-if="Number(item.original_total_incl_tax) > Number(item.total_incl_tax)"
                                          class="text-muted text-decoration-line-through fs-6 me-2">{{ item.formatted_original_total_incl_tax }}</span>
                                    {{ item.formatted_total_incl_tax }}
                                </div>
                                <button type="button" class="btn btn-sm btn-link text-danger p-0 mt-1"
                                        @click.stop="removeCartItem(item.id)" aria-label="Supprimer l'article">
                                    <i class="bi bi-trash me-1"></i>Retirer
                                </button>
                            </div>
                        </div>

                        <div class="col-md-12 mt-4" v-if="item.type == 'stage' || item.type == 'gift_card'">
                            <div class="card border-primary beneficiary-card">
                                <div class="card-header bg-light">
                                    <div class="d-flex justify-content-between align-items-center">
                                        <h5 class="mb-0"><i class="bi bi-person-fill me-2"></i>Informations du
                                            bénéficiaire</h5>

                                        <!-- Indicateurs de statut de sauvegarde -->
                                        <div class="d-flex align-items-center gap-2">
                                            <span v-if="savingBeneficiaire[item.id]" class="badge bg-info">
                                                <i class="bi bi-arrow-repeat me-1 spin"></i> Enregistrement...
                                            </span>
                                            <span
                                                v-else-if="showBeneficiaireForms[item.id] && !beneficiaireSaved[item.id]"
                                                class="badge bg-warning text-dark">
                                                <i class="bi bi-exclamation-triangle-fill me-1"></i> Non enregistré
                                            </span>
                                            <span
                                                v-else-if="showBeneficiaireForms[item.id] && beneficiaireSaved[item.id]"
                                                class="badge bg-success">
                                                <i class="bi bi-check-circle-fill me-1"></i> Enregistré
                                            </span>
                                        </div>
                                    </div>
                                </div>
                                <div class="card-body">
                                    <!-- Switch pour sélectionner si l'utilisateur est le bénéficiaire ou non -->
                                    <div class="mb-3">
                                        <div class="form-check form-switch">
                                            <input
                                                class="form-check-input"
                                                type="checkbox"
                                                role="switch"
                                                :id="'isBeneficiaire-' + item.id"
                                                v-model="showBeneficiaireForms[item.id]"
                                                @change="toggleBeneficiaireForm(item.id)"
                                            >
                                            <label class="form-check-label ps-2" :for="'isBeneficiaire-' + item.id">
                                                Je ne suis pas le bénéficiaire
                                            </label>
                                        </div>
                                    </div>

                                    <!-- Formulaire affiché seulement si showBeneficiaireForms[item.id] est true -->
                                    <form @submit.prevent="addBeneficiaire(item.id)"
                                          v-if="showBeneficiaireForms[item.id]">

                                        <!-- Liste des bénéficiaires existants -->
                                        <div class="mb-3" v-if="existingBeneficiaires.length > 0">
                                            <label class="form-label">Bénéficiaires existants</label>
                                            <div class="beneficiaires-list">
                                                <div
                                                    v-for="beneficiaire in existingBeneficiaires"
                                                    :key="beneficiaire.id"
                                                    class="beneficiaire-item"
                                                    :class="{'selected': selectedBeneficiaire[item.id] === beneficiaire.id}"
                                                    @click="selectBeneficiaire(beneficiaire, item.id)"
                                                >
                                                    <div class="beneficiaire-info">
                                                        <div class="beneficiaire-name">
                                                            {{ beneficiaire.gender === 'female' ? 'Mme.' : 'M.' }}
                                                            {{ beneficiaire.first_name }} {{ beneficiaire.last_name }}
                                                        </div>
                                                        <div class="beneficiaire-email">
                                                            {{ beneficiaire.email }}
                                                        </div>
                                                        <div v-if="beneficiaire.address"
                                                             class="beneficiaire-address text-muted small">
                                                            {{ beneficiaire.address }}
                                                            <span v-if="beneficiaire.address_complement">, {{
                                                                    beneficiaire.address_complement
                                                                }}</span>
                                                        </div>
                                                        <div v-if="beneficiaire.city"
                                                             class="beneficiaire-location text-muted small">
                                                            {{ beneficiaire.postcode }} {{ beneficiaire.city }}
                                                        </div>
                                                    </div>
                                                    <div class="select-indicator">
                                                        <i class="bi bi-check-circle-fill"
                                                           v-if="selectedBeneficiaire[item.id] === beneficiaire.id"></i>
                                                    </div>
                                                </div>
                                            </div>
                                            <div class="divider my-3">
                                                <span>ou</span>
                                            </div>
                                        </div>

                                        <div class="row">
                                            <!-- Genre -->
                                            <div class="col-md-3 mb-2">
                                                <label :for="'gender-' + item.id" class="form-label">Genre</label>
                                                <select
                                                    class="form-select form-select-sm"
                                                    :id="'gender-' + item.id"
                                                    v-model="beneficiaires[item.id].gender"
                                                >
                                                    <option value="male">M.</option>
                                                    <option value="female">Mme.</option>
                                                </select>
                                            </div>

                                            <!-- Prénom -->
                                            <div class="col-md-4 mb-2">
                                                <label for="first_name" class="form-label">Prénom *</label>
                                                <input
                                                    type="text"
                                                    class="form-control form-control-sm"
                                                    :id="'first_name-' + item.id"
                                                    v-model="beneficiaires[item.id].first_name"
                                                    :class="{'is-invalid': beneficiaireErrors[item.id].first_name}"
                                                    placeholder="Prénom du bénéficiaire"
                                                    @input="beneficiaireSaved[item.id] = false"
                                                >
                                                <div class="invalid-feedback"
                                                     v-if="beneficiaireErrors[item.id].first_name">
                                                    {{ beneficiaireErrors[item.id].first_name }}
                                                </div>
                                            </div>

                                            <!-- Nom -->
                                            <div class="col-md-5 mb-2">
                                                <label for="last_name" class="form-label">Nom *</label>
                                                <input
                                                    type="text"
                                                    class="form-control form-control-sm"
                                                    :id="'last_name-' + item.id"
                                                    v-model="beneficiaires[item.id].last_name"
                                                    :class="{'is-invalid': beneficiaireErrors[item.id].last_name}"
                                                    placeholder="Nom du bénéficiaire"
                                                    @input="beneficiaireSaved[item.id] = false"
                                                >
                                                <div class="invalid-feedback"
                                                     v-if="beneficiaireErrors[item.id].last_name">
                                                    {{ beneficiaireErrors[item.id].last_name }}
                                                </div>
                                            </div>

                                            <!-- Email -->
                                            <div class="col-md-12 mb-2">
                                                <label for="email" class="form-label">Email (optionnel)</label>
                                                <input
                                                    type="email"
                                                    class="form-control form-control-sm"
                                                    :id="'email-' + item.id"
                                                    v-model="beneficiaires[item.id].email"
                                                    :class="{'is-invalid': beneficiaireErrors[item.id].email}"
                                                    placeholder="Email du bénéficiaire (optionnel)"
                                                    @input="beneficiaireSaved[item.id] = false"
                                                >
                                                <div class="invalid-feedback" v-if="beneficiaireErrors[item.id].email">
                                                    {{ beneficiaireErrors[item.id].email }}
                                                </div>
                                            </div>

                                            <!-- Adresse et complément d'adresse -->
                                            <div class="col-md-8 mb-2">
                                                <label :for="'address-' + item.id" class="form-label">Adresse *</label>
                                                <input
                                                    type="text"
                                                    class="form-control form-control-sm"
                                                    :id="'address-' + item.id"
                                                    v-model="beneficiaires[item.id].address"
                                                    :class="{'is-invalid': beneficiaireErrors[item.id].address}"
                                                    placeholder="Numéro et nom de rue"
                                                    required
                                                    @input="beneficiaireSaved[item.id] = false"
                                                >
                                                <div class="invalid-feedback"
                                                     v-if="beneficiaireErrors[item.id].address">
                                                    {{ beneficiaireErrors[item.id].address }}
                                                </div>
                                            </div>

                                            <div class="col-md-4 mb-2">
                                                <label :for="'address_complement-' + item.id" class="form-label">Complément</label>
                                                <input
                                                    type="text"
                                                    class="form-control form-control-sm"
                                                    :id="'address_complement-' + item.id"
                                                    v-model="beneficiaires[item.id].address_complement"
                                                    :class="{'is-invalid': beneficiaireErrors[item.id].address_complement}"
                                                    placeholder="Apt, bât..."
                                                    @input="beneficiaireSaved[item.id] = false"
                                                >
                                                <div class="invalid-feedback"
                                                     v-if="beneficiaireErrors[item.id].address_complement">
                                                    {{ beneficiaireErrors[item.id].address_complement }}
                                                </div>
                                            </div>

                                            <!-- Code postal et ville -->
                                            <div class="col-md-4 mb-2">
                                                <label :for="'postcode-' + item.id" class="form-label">Code postal
                                                    *</label>
                                                <input
                                                    type="text"
                                                    class="form-control form-control-sm"
                                                    :id="'postcode-' + item.id"
                                                    v-model="beneficiaires[item.id].postcode"
                                                    :class="{'is-invalid': beneficiaireErrors[item.id].postcode}"
                                                    placeholder="Code postal"
                                                    maxlength="5"
                                                    required
                                                    @input="beneficiaireSaved[item.id] = false"
                                                >
                                                <div class="invalid-feedback"
                                                     v-if="beneficiaireErrors[item.id].postcode">
                                                    {{ beneficiaireErrors[item.id].postcode }}
                                                </div>
                                            </div>

                                            <div class="col-md-8 mb-2">
                                                <label :for="'city-' + item.id" class="form-label">Ville *</label>
                                                <input
                                                    type="text"
                                                    class="form-control form-control-sm"
                                                    :id="'city-' + item.id"
                                                    v-model="beneficiaires[item.id].city"
                                                    :class="{'is-invalid': beneficiaireErrors[item.id].city}"
                                                    placeholder="Ville"
                                                    required
                                                    @input="beneficiaireSaved[item.id] = false"
                                                >
                                                <div class="invalid-feedback" v-if="beneficiaireErrors[item.id].city">
                                                    {{ beneficiaireErrors[item.id].city }}
                                                </div>
                                            </div>
                                        </div>

                                        <p class="text-muted small mt-2 mb-1">* Champs obligatoires</p>

                                        <div class="d-flex justify-content-end mt-2">
                                            <button @click.prevent="addBeneficiaire(item.id)" type="button"
                                                    class="btn btn-primary btn-sm">
                                                <i class="bi bi-check-circle me-1"></i>Enregistrer
                                            </button>
                                        </div>
                                    </form>

                                    <!-- Message affiché si l'utilisateur est le bénéficiaire -->
                                    <div v-else class="alert alert-info mb-0">
                                        <i class="bi bi-info-circle me-2"></i>
                                        <span v-if="item.type === 'stage'">Vous êtes le bénéficiaire de cette réservation.</span>
                                        <span v-else-if="item.type === 'gift_card'">Vous êtes le bénéficiaire de cette carte cadeau.</span>
                                        <span v-else>Vous êtes le bénéficiaire de cet article.</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <!-- Bloc pour les préférences d'envoi d'email des cartes cadeaux -->
                        <div class="col-md-12 mt-4" v-if="item.type === 'gift_card' && showBeneficiaireForms[item.id]">
                            <div class="card border-success gift-card-email-card">
                                <div class="card-header bg-light">
                                    <div class="d-flex justify-content-between align-items-center">
                                        <h5 class="mb-0"><i class="bi bi-envelope-fill me-2"></i>Envoi du bon cadeau
                                        </h5>
                                    </div>
                                </div>
                                <div class="card-body">
                                    <div class="row">
                                        <!-- Options d'envoi -->
                                        <div class="col-md-12 mb-3">
                                            <fieldset>
                                                <legend class="form-label fw-bold">Envoyer le bon cadeau par mail à :
                                                </legend>
                                                <div class="mt-2">
                                                    <div class="form-check">
                                                        <input
                                                            class="form-check-input"
                                                            type="radio"
                                                            :name="'sendTo-' + item.id"
                                                            :id="'sendTo-buyer-' + item.id"
                                                            value="buyer"
                                                            v-model="giftCardEmailOptions[item.id].sendTo"
                                                            :class="{'is-invalid': giftCardEmailErrors[item.id]?.sendTo}"
                                                        >
                                                        <label class="form-check-label"
                                                               :for="'sendTo-buyer-' + item.id">
                                                            À moi (la personne qui offre)
                                                            <span v-if="user" class="text-muted ms-2">({{
                                                                    user.email
                                                                }})</span>
                                                        </label>
                                                    </div>
                                                    <div class="form-check">
                                                        <input
                                                            class="form-check-input"
                                                            type="radio"
                                                            :name="'sendTo-' + item.id"
                                                            :id="'sendTo-beneficiary-' + item.id"
                                                            value="beneficiary"
                                                            v-model="giftCardEmailOptions[item.id].sendTo"
                                                            :class="{'is-invalid': giftCardEmailErrors[item.id]?.sendTo}"
                                                        >
                                                        <label class="form-check-label"
                                                               :for="'sendTo-beneficiary-' + item.id">
                                                            Directement au bénéficiaire
                                                            <span v-if="beneficiaires[item.id]?.email"
                                                                  class="text-muted ms-2">({{
                                                                    beneficiaires[item.id].email
                                                                }})</span>
                                                        </label>
                                                    </div>
                                                    <div v-if="giftCardEmailErrors[item.id]?.sendTo"
                                                         class="invalid-feedback d-block">
                                                        {{ giftCardEmailErrors[item.id].sendTo }}
                                                    </div>
                                                </div>
                                            </fieldset>
                                        </div>

                                        <!-- Message optionnel -->
                                        <div class="col-md-12 mb-3">
                                            <label :for="'giftCardMessage-' + item.id" class="form-label">
                                                Message personnalisé (optionnel)
                                            </label>
                                            <textarea
                                                class="form-control"
                                                :id="'giftCardMessage-' + item.id"
                                                v-model="giftCardEmailOptions[item.id].message"
                                                :class="{'is-invalid': giftCardEmailErrors[item.id]?.message}"
                                                placeholder="Ajoutez un message personnel pour accompagner le bon cadeau..."
                                                maxlength="40"
                                                rows="2"
                                            ></textarea>
                                            <div class="form-text d-flex justify-content-between">
                                                <span>Maximum 40 caractères</span>
                                                <span
                                                    :class="{'text-danger': giftCardEmailOptions[item.id].message && giftCardEmailOptions[item.id].message.length > 35}">
                                                    {{
                                                        giftCardEmailOptions[item.id].message ? giftCardEmailOptions[item.id].message.length : 0
                                                    }}/40
                                                </span>
                                            </div>
                                            <div v-if="giftCardEmailErrors[item.id]?.message" class="invalid-feedback">
                                                {{ giftCardEmailErrors[item.id].message }}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    </div>
                </div>
                <div v-else class="card shadow-sm border-0 mb-4">
                    <div class="card-body empty-cart-message text-center py-5">
                        <i class="bi bi-cart-x display-4 mb-3"></i>
                        <h3>Votre panier est vide</h3>
                        <p class="text-muted">Ajoutez des produits à votre panier pour continuer</p>
                    </div>
                </div>
                <div v-if="optionReviewMessage" class="alert alert-info" role="status">{{ optionReviewMessage }}</div>
                <div class="card shadow-sm border-0 mb-4" v-if="options.length && cart">
                    <div class="card-header bg-white border-bottom">
                        <h3 class="h5 mb-0"><i class="bi bi-plus-circle-fill me-2"></i>Options supplémentaires</h3>
                    </div>
                    <div class="card-body">
                        <p class="text-muted mb-4">Sélectionnez des options additionnelles pour personnaliser votre
                            expérience</p>

                        <div class="options-container">
                            <div
                                v-for="option in options"
                                :key="option.offer_key"
                                class="option-item"
                                :class="{'option-selected': optionChoices.includes(option.offer_key)}"
                                @click="toggleOption(option.offer_key)"
                            >
                                <div class="option-content">
                                    <div class="option-image-container">
                                        <img :src="option.image" :alt="option.designation" class="option-image">
                                    </div>
                                    <div class="option-info">
                                        <div class="option-title">{{ option.designation }}</div>
                                        <p v-if="option.coverage_label" class="small mb-1">{{ option.coverage_label }}</p>
                                        <div class="option-description" v-if="option.description"
                                             v-safe-html="option.description"></div>
                                        <div class="option-price">{{ formatPrice(option.montant_ttc) }}€</div>
                                    </div>
                                    <div class="option-checkbox">
                                        <input
                                            type="checkbox"
                                            :id="'option-' + option.offer_key"
                                            :value="option.offer_key"
                                            v-model="optionChoices"
                                            @click.stop
                                            :aria-label="[option.designation, option.coverage_label].filter(Boolean).join(' — ')"
                                            class="form-check-input option-selection"
                                        >
                                    </div>
                                </div>
                            </div>
                        </div>

                        <!-- Bouton pour ajouter les options sélectionnées -->
                        <div class="mt-4 text-center" v-if="optionChoices.length > 0">
                            <button
                                @click="addSelectedOptionsToCart"
                                class="btn-add-options"
                            >
                                <i class="bi bi-cart-plus me-2"></i>
                                Ajouter les options sélectionnées ({{ optionChoices.length }})
                            </button>
                        </div>
                    </div>
                </div>
                <div v-if="!user" class="user-auth-section mt-4 mb-4">
                    <div class="auth-card">
                        <div class="auth-card-content">
                            <div class="auth-icon">
                                <i class="bi bi-person-circle"></i>
                            </div>
                            <div class="auth-text">
                                <h4 class="auth-title">Vous n'êtes pas connecté</h4>
                                <p class="auth-description">Connectez-vous ou créez un compte pour finaliser votre
                                    commande
                                    et suivre vos réservations.</p>
                            </div>
                        </div>
                        <div class="auth-actions">
                            <a href="/customer/login" class="btn-auth btn-login">
                                <i class="bi bi-box-arrow-in-right me-2"></i>Se connecter
                            </a>
                            <a href="/customer/register" class="btn-auth btn-register">
                                <i class="bi bi-person-plus me-2"></i>S'inscrire
                            </a>
                        </div>
                    </div>
                </div>
                <!-- Vos coordonnées -->
                <div class="card shadow-sm border-0 mb-4" v-if="cart && user">
                    <div class="card-header bg-white border-bottom">
                        <h3 class="h5 mb-0"><i class="bi bi-geo-alt-fill me-2"></i>Vos coordonnées</h3>
                    </div>
                    <div class="card-body">

                        <!-- Affichage des adresses existantes -->
                        <div v-if="customerAddresses.length > 0 && !showAddressForm"
                             class="existing-addresses-container">
                            <div class="address-selection-header">
                                <h5 class="mb-3">Adresses enregistrées</h5>
                                <p class="text-muted">Sélectionnez une adresse existante ou ajoutez-en une nouvelle</p>
                            </div>

                            <div class="addresses-grid">
                                <div
                                    v-for="address in customerAddresses"
                                    :key="address.id"
                                    class="address-card"
                                    :class="{'address-selected': selectedAddressId === address.id}"
                                    @click="selectAddress(address.id)"
                                >
                                    <div class="address-card-header">
                                        <div class="selection-indicator">
                                            <div class="radio-button">
                                                <input
                                                    type="radio"
                                                    :id="'address-' + address.id"
                                                    :name="'selected-address'"
                                                    :checked="selectedAddressId === address.id"
                                                    class="visually-hidden"
                                                >
                                                <label :for="'address-' + address.id" class="custom-radio"></label>
                                            </div>
                                            <span class="address-name">{{ address.first_name }} {{
                                                    address.last_name
                                                }}</span>
                                        </div>
                                        <div v-if="address.default_address" class="default-badge">
                                            Par défaut
                                        </div>
                                    </div>

                                    <div class="address-card-body">
                                        <div class="address-details">
                                            <p v-if="address.company_name" class="company-name">
                                                <i class="bi bi-building me-1"></i> {{ address.company_name }}
                                            </p>
                                            <p class="address-line">
                                                <i class="bi bi-house-door me-1"></i>
                                                {{
                                                    Array.isArray(address.address) ? address.address[0] : address.address
                                                }}
                                            </p>
                                            <p class="city-postal">
                                                <i class="bi bi-geo me-1"></i>
                                                {{ address.postcode }} {{ address.city }}
                                            </p>
                                            <p class="country-state">
                                                <i class="bi bi-globe me-1"></i>
                                                {{ address.country }} {{ address.state ? '- ' + address.state : '' }}
                                            </p>
                                        </div>
                                        <div class="contact-details">
                                            <p class="phone">
                                                <i class="bi bi-telephone me-1"></i> {{ address.phone }}
                                            </p>
                                            <p class="email">
                                                <i class="bi bi-envelope me-1"></i> {{ address.email }}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div class="actions-container">
                                <button @click="useSelectedAddress" class="btn-use-address"
                                        :disabled="!selectedAddressId">
                                    <i class="bi bi-check-circle me-2"></i>Utiliser cette adresse
                                </button>

                                <span class="or-separator">ou</span>

                                <button @click="toggleAddressForm" class="btn-address">
                                    <i class="bi bi-plus-circle me-2"></i> Ajouter une nouvelle adresse
                                </button>
                            </div>
                        </div>

                        <div v-if="customerAddresses.length === 0 && !showAddressForm" class="address-button-container">
                            <button @click="toggleAddressForm" class="btn-address">
                                <i class="bi bi-plus-circle me-2"></i> Ajouter une nouvelle adresse
                            </button>
                        </div>

                        <div v-if="showAddressForm" class="address-form-card">
                            <div class="address-form-header">
                                <div class="d-flex justify-content-between align-items-center">
                                    <h5 class="mb-0"><i class="bi bi-geo-alt me-2"></i>Nouvelle adresse</h5>
                                    <button @click="toggleAddressForm" class="btn-close-form">
                                        <i class="bi bi-x-lg"></i>
                                    </button>
                                </div>
                            </div>
                            <div v-if="customerAddresses.length > 0" class="back-to-addresses">
                                <button @click="toggleAddressForm" class="btn-back">
                                    <i class="bi bi-arrow-left me-2"></i>Retour à mes adresses
                                </button>
                            </div>
                            <div class="address-form-body">
                                <form @submit.prevent="saveAddress">
                                    <div class="row">
                                        <div class="col-md-6 mb-3">
                                            <label for="company_name" class="form-label">
                                                <i class="bi bi-building me-1"></i> Nom de société (optionnel)
                                            </label>
                                            <input
                                                type="text"
                                                class="form-control"
                                                id="company_name"
                                                v-model="address.company_name"
                                                placeholder="Nom de votre entreprise"
                                            >
                                        </div>

                                        <div class="col-md-6 mb-3">
                                            <label for="email" class="form-label required">
                                                <i class="bi bi-envelope me-1"></i> Email
                                            </label>
                                            <input
                                                type="email"
                                                class="form-control"
                                                id="email"
                                                v-model="address.email"
                                                :class="{'is-invalid': addressErrors.email}"
                                                placeholder="votreemail@exemple.com"
                                            >
                                            <div class="invalid-feedback" v-if="addressErrors.email">
                                                {{ addressErrors.email }}
                                            </div>
                                        </div>

                                        <div class="col-md-6 mb-3">
                                            <label for="first_name" class="form-label required">
                                                <i class="bi bi-person me-1"></i> Prénom
                                            </label>
                                            <input
                                                type="text"
                                                class="form-control"
                                                id="first_name"
                                                v-model="address.first_name"
                                                :class="{'is-invalid': addressErrors.first_name}"
                                                placeholder="Votre prénom"
                                            >
                                            <div class="invalid-feedback" v-if="addressErrors.first_name">
                                                {{ addressErrors.first_name }}
                                            </div>
                                        </div>

                                        <div class="col-md-6 mb-3">
                                            <label for="last_name" class="form-label required">
                                                <i class="bi bi-person-fill me-1"></i> Nom
                                            </label>
                                            <input
                                                type="text"
                                                class="form-control"
                                                id="last_name"
                                                v-model="address.last_name"
                                                :class="{'is-invalid': addressErrors.last_name}"
                                                placeholder="Votre nom"
                                            >
                                            <div class="invalid-feedback" v-if="addressErrors.last_name">
                                                {{ addressErrors.last_name }}
                                            </div>
                                        </div>

                                        <div class="col-12 mb-3">
                                            <label for="address" class="form-label required">
                                                <i class="bi bi-house-door me-1"></i> Adresse
                                            </label>
                                            <input
                                                type="text"
                                                class="form-control"
                                                id="address"
                                                v-model="address.address[0]"
                                                :class="{'is-invalid': addressErrors.address}"
                                                placeholder="Numéro et nom de rue"
                                            >
                                            <div class="invalid-feedback" v-if="addressErrors.address">
                                                {{ addressErrors.address }}
                                            </div>
                                        </div>

                                        <div class="col-md-6 mb-3">
                                            <label for="country" class="form-label required">
                                                <i class="bi bi-globe me-1"></i> Pays
                                            </label>
                                            <select
                                                class="form-select"
                                                id="country"
                                                v-model="address.country"
                                                :class="{'is-invalid': addressErrors.country}"
                                            >
                                                <option value="">Sélectionnez un pays</option>
                                                <option
                                                    v-for="country in countries"
                                                    :key="country.code"
                                                    :value="country.code"
                                                >
                                                    {{ country.name }}
                                                </option>
                                            </select>
                                            <div class="invalid-feedback" v-if="addressErrors.country">
                                                {{ addressErrors.country }}
                                            </div>
                                        </div>

                                        <div class="col-md-6 mb-3">
                                            <label for="state" class="form-label"
                                                   :class="{'required': states.length > 0}">
                                                <i class="bi bi-map me-1"></i> Région/État
                                            </label>
                                            <select
                                                class="form-select"
                                                id="state"
                                                v-model="address.state"
                                                :class="{'is-invalid': addressErrors.state}"
                                                :disabled="!address.country || states.length === 0"
                                            >
                                                <option value="">Sélectionnez une région</option>
                                                <option
                                                    v-for="state in states"
                                                    :key="state.code"
                                                    :value="state.code"
                                                >
                                                    {{ state.default_name }}
                                                </option>
                                            </select>
                                            <div class="invalid-feedback" v-if="addressErrors.state">
                                                {{ addressErrors.state }}
                                            </div>
                                        </div>

                                        <div class="col-md-4 mb-3">
                                            <label for="city" class="form-label required">
                                                <i class="bi bi-building me-1"></i> Ville
                                            </label>
                                            <input
                                                type="text"
                                                class="form-control"
                                                id="city"
                                                v-model="address.city"
                                                :class="{'is-invalid': addressErrors.city}"
                                                placeholder="Votre ville"
                                            >
                                            <div class="invalid-feedback" v-if="addressErrors.city">
                                                {{ addressErrors.city }}
                                            </div>
                                        </div>

                                        <div class="col-md-4 mb-3">
                                            <label for="postcode" class="form-label required">
                                                <i class="bi bi-mailbox me-1"></i> Code postal
                                            </label>
                                            <input
                                                type="text"
                                                class="form-control"
                                                id="postcode"
                                                v-model="address.postcode"
                                                :class="{'is-invalid': addressErrors.postcode}"
                                                placeholder="Code postal"
                                            >
                                            <div class="invalid-feedback" v-if="addressErrors.postcode">
                                                {{ addressErrors.postcode }}
                                            </div>
                                        </div>

                                        <div class="col-md-4 mb-3">
                                            <label for="phone" class="form-label required">
                                                <i class="bi bi-telephone me-1"></i> Téléphone
                                            </label>
                                            <input
                                                type="tel"
                                                class="form-control"
                                                id="phone"
                                                v-model="address.phone"
                                                :class="{'is-invalid': addressErrors.phone}"
                                                placeholder="Votre numéro de téléphone"
                                            >
                                            <div class="invalid-feedback" v-if="addressErrors.phone">
                                                {{ addressErrors.phone }}
                                            </div>
                                        </div>
                                    </div>

                                    <div class="d-flex justify-content-end">
                                        <button type="submit" class="btn-save-address">
                                            <i class="bi bi-check-circle me-2"></i>Enregistrer l'adresse
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Méthodes de paiement -->
                <div class="card shadow-sm border-0 mb-4" v-if="cart && user">
                    <div class="card-header bg-white border-bottom">
                        <h3 class="h5 mb-0"><i class="bi bi-credit-card me-2"></i>Choisissez votre méthode de paiement</h3>
                    </div>
                    <div class="card-body">
                        <div class="payment-methods-container">
                            <div
                                v-for="payment in payments"
                                :key="payment.method"
                                class="payment-method-item"
                                :class="{'payment-method-selected': selectedPaymentMethod === payment.method}"
                                @click="selectedPaymentMethod = payment"
                            >
                                <div class="payment-method-content">
                                    <div class="payment-method-image-container">
                                        <img :src="payment.image" :alt="payment.method_title"
                                             class="payment-method-image">
                                    </div>
                                    <div class="payment-method-info">
                                        <div class="payment-method-title">{{ payment.method_title }}</div>
                                        <div class="payment-method-description">{{ payment.description }}</div>
                                    </div>
                                    <div class="payment-method-radio">
                                        <div class="custom-radio-container">
                                            <input
                                                type="radio"
                                                :id="'payment-' + payment.method"
                                                :value="payment"
                                                v-model="selectedPaymentMethod"
                                                name="payment_method"
                                                class="custom-radio-input"
                                            >
                                            <label :for="'payment-' + payment.method" class="custom-radio-label">
                                                <span class="custom-radio-button"></span>
                                            </label>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Sidebar récapitulatif -->
            <div class="col-lg-4">
                <div class="card shadow-sm border-0 cart-summary-sticky" v-if="cart">
                    <div class="card-header bg-white border-bottom">
                        <h3 class="h5 mb-0"><i class="bi bi-calculator me-2"></i>Récapitulatif</h3>
                    </div>
                    <div class="card-body">
                        <div class="d-flex justify-content-between mb-2">
                            <span>Sous-total</span>
                            <span>{{ cart.formatted_sub_total }}</span>
                        </div>
                        <div class="d-flex justify-content-between mb-2" v-if="cart.tax_total > 0">
                            <span>TVA</span>
                            <span>{{ cart.formatted_tax_total }}</span>
                        </div>
                        <div class="d-flex justify-content-between mb-2 text-success" v-if="cart.discount_amount > 0">
                            <span>Remise</span>
                            <span>-{{ cart.formatted_discount_amount }}</span>
                        </div>
                        <hr>
                        <div class="d-flex justify-content-between fw-bold fs-5 mb-3">
                            <span>Total</span>
                            <span class="text-primary">{{ cart.formatted_grand_total }}</span>
                        </div>

                        <PromoCodeSection
                            :initial-code="cart.coupon_code || ''"
                            :initial-discount="cart.discount_amount || 0"
                            @notification="showNotification"
                            @discount-updated="updatePromoCodeDiscount"
                            @update-cart="pointUpdateCart"
                        />
                        <GiftCardSection
                            v-if="user && cart.items?.length"
                            class="mt-3 d-block"
                            :initial-discount="giftCardDiscount"
                            @notification="showNotification"
                            @discount-updated="updateGiftCardDiscount"
                            @update-cart="pointUpdateCart"
                        />


                        <PointsSection
                            v-if="user"
                            class="mt-3 d-block"
                            :initial-discount="pointsDiscount"
                            @notification="showNotification"
                            @discount-updated="updatePointsDiscount"
                            @update-cart="pointUpdateCart"
                        />

                        <div class="checkout-points-note mt-3" v-if="cart.points_reward">
                            Cette commande vous donne <span class="checkout-total-point-amount">{{ cart.points_reward }}</span> points.
                        </div>

                        <div class="terms-container mt-3">
                            <label class="custom-checkbox">
                                <input type="checkbox" class="custom-checkbox-input" v-model="termsAccepted">
                                <span class="custom-checkbox-box"></span>
                                <span class="custom-checkbox-label">
                                    J'ai lu et j'accepte les <a href="/cgv" target="_blank" rel="noopener noreferrer" class="terms-link" @click.stop>conditions générales de vente</a>.
                                </span>
                            </label>
                        </div>

                        <button @click.prevent="handleSubmitCart" :disabled="!cart || !user"
                                class="mt-3 btn-checkout w-100"
                                type="button">
                            <i class="bi bi-lock-fill me-2"></i>
                            Procéder au paiement sécurisé
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped>
/* Styles pour les notifications */
.notification-toast {
    position: fixed;
    top: 20px;
    right: 20px;
    z-index: 9999;
    display: flex;
    align-items: center;
    max-width: 350px;
    padding: 15px;
    border-radius: 8px;
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.15);
    animation: slide-in 0.3s ease-out forwards;
}

.notification-success {
    background-color: #d4edda;
    color: #155724;
    border-left: 5px solid #28a745;
}

.notification-error {
    background-color: #f8d7da;
    color: #721c24;
    border-left: 5px solid #dc3545;
}

.notification-info {
    background-color: #d1ecf1;
    color: #0c5460;
    border-left: 5px solid #17a2b8;
}

.notification-warning {
    background-color: #fff3cd;
    color: #856404;
    border-left: 5px solid #ffc107;
}

.notification-icon {
    flex-shrink: 0;
    font-size: 1.5rem;
    margin-right: 12px;
}

.notification-content {
    flex-grow: 1;
    font-size: 0.95rem;
}

.notification-close {
    flex-shrink: 0;
    background: transparent;
    border: none;
    color: inherit;
    opacity: 0.7;
    cursor: pointer;
    padding: 0;
    margin-left: 10px;
    font-size: 1.2rem;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: opacity 0.2s;
}

.notification-close:hover {
    opacity: 1;
}

@keyframes slide-in {
    from {
        transform: translateX(100%);
        opacity: 0;
    }
    to {
        transform: translateX(0);
        opacity: 1;
    }
}

.fade-enter-active, .fade-leave-active {
    transition: opacity 0.3s, transform 0.3s;
}

.fade-enter-from, .fade-leave-to {
    opacity: 0;
    transform: translateX(30px);
}

/* Style pour le bouton de suppression d'article */
.btn-remove-item {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    background-color: #f8f9fa;
    border: 1px solid #e0e0e0;
    color: #dc3545;
    font-size: 1.1rem;
    border-radius: 50%;
    cursor: pointer;
    transition: all 0.2s ease;
}

.btn-remove-item:hover {
    background-color: #dc3545;
    color: white;
    border-color: #dc3545;
    transform: scale(1.1);
    box-shadow: 0 2px 5px rgba(220, 53, 69, 0.3);
}

/* Styles pour la liste des bénéficiaires */
.beneficiaires-list {
    max-height: 200px;
    overflow-y: auto;
    border: 1px solid #e0e0e0;
    border-radius: 6px;
    background-color: #fff;
}

.beneficiaire-item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0.75rem 1rem;
    border-bottom: 1px solid #e0e0e0;
    cursor: pointer;
    transition: all 0.2s ease;
}

.beneficiaire-item:last-child {
    border-bottom: none;
}

.beneficiaire-item:hover {
    background-color: #f5f8ff;
}

.beneficiaire-item.selected {
    background-color: #e8f4ff;
    border-left: 3px solid #4e73df;
}

.beneficiaire-info {
    display: flex;
    flex-direction: column;
}

.beneficiaire-name {
    font-weight: 600;
    color: #333;
}

.beneficiaire-email {
    font-size: 0.85rem;
    color: #6c757d;
}

.select-indicator {
    color: #4e73df;
    font-size: 1.2rem;
}

.divider {
    display: flex;
    align-items: center;
    text-align: center;
    color: #6c757d;
}

.divider::before,
.divider::after {
    content: '';
    flex: 1;
    border-bottom: 1px solid #e0e0e0;
}

.divider span {
    padding: 0 10px;
    font-size: 0.9rem;
    font-weight: 500;
}

.product-name {
    margin-bottom: 0;
    font-weight: 600;
    color: #333;
}

.price {
    font-size: 1.2em;
    font-weight: bold;
    color: #4e73df;
}

.total-price {
    font-size: 1.3em;
}

/* Styles pour les prix des stages */
.stage-price-container {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
}

.original-price {
    font-size: 1em !important;
    color: #999 !important;
    text-decoration: line-through;
    font-weight: 500 !important;
    margin-bottom: 0.25rem;
}

.ttc-price {
    font-size: 1.3em;
    color: #28a745 !important;
    font-weight: 700;
    margin-bottom: 0.25rem;
}

.price-label {
    font-size: 0.8em;
    color: #28a745;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
}

.tours-control {
    display: flex;
    align-items: center;
}

.tours-label {
    margin-right: 10px;
    font-size: 0.9em;
    color: #666;
    font-weight: 500;
}

.tours-input-group {
    display: flex;
    align-items: center;
    width: 140px;
    position: relative;
    border-radius: 6px;
    overflow: hidden;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
}

.tours-input {
    width: 60px;
    height: 38px;
    text-align: center;
    border: 1px solid #e0e0e0;
    border-radius: 0;
    font-weight: 600;
    color: #4e73df;
    padding: 6px 0;
}

.btn-tours {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 38px;
    background-color: #f8f9fa;
    border: 1px solid #e0e0e0;
    color: #4e73df;
    font-size: 1.1rem;
    cursor: pointer;
    transition: all 0.2s ease;
}

.btn-tours:hover:not(:disabled) {
    background-color: #e8f1ff;
    color: #3a57c2;
}

.btn-tours:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    color: #999;
}

.btn-tours-minus {
    border-radius: 6px 0 0 6px;
    border-right: none;
}

.btn-tours-plus {
    border-radius: 0 6px 6px 0;
    border-left: none;
}

.input-group {
    width: 120px;
}

/* Styles pour les sections */
.section-title {
    font-size: 1.5rem;
    color: #333;
    margin-bottom: 1.5rem;
    padding-bottom: 0.75rem;
    border-bottom: 2px solid #f0f0f0;
}

/* Styles pour les éléments du panier */
.cart-item {
    border-bottom: 1px solid #f0f0f0;
}

.cart-item:last-child {
    border-bottom: none;
}

/* Récapitulatif sticky (sidebar) */
.cart-summary-sticky {
    position: sticky;
    top: 1rem;
}

.checkout-points-note {
    font-size: 0.9rem;
    color: #4e73df;
}

.cart-item-header {
    border-bottom: 1px solid #e0e0e0;
    padding-bottom: 1rem;
    margin-bottom: 1rem;
}

.cart-item-details {
    padding: 0.5rem 0;
}

.info-block {
    margin-bottom: 1rem;
    padding: 0.75rem;
    background-color: #fff;
    border-radius: 6px;
    border: 1px solid #eaeaea;
}

.info-label {
    font-weight: 600;
    color: #4e73df;
    margin-right: 5px;
}

.figure {
    margin: 0;
    max-width: 120px;
    border-radius: 6px;
    overflow: hidden;
}

.car-brand, .circuit-name, .option-name {
    font-weight: 500;
}

.date-info {
    display: flex;
    align-items: center;
}

.beneficiary-card {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.form-check.form-switch {
    padding-left: 2.5em;
}

.form-switch .form-check-input {
    height: 1.5em;
    width: 3em;
    cursor: pointer;
}

.form-switch .form-check-input:checked {
    background-color: #4e73df;
    border-color: #4e73df;
}

.form-check-label {
    cursor: pointer;
    font-weight: 500;
}

.alert-info {
    background-color: #e8f4ff;
    border-color: #c0e0ff;
    color: #4e73df;
}

.empty-cart-message {
    padding: 3rem;
    color: #666;
}

/* Styles pour le formulaire de bénéficiaire */
.beneficiaire-button-container {
    margin: 0.5rem 0;
}

.btn-beneficiaire {
    display: inline-flex;
    align-items: center;
    background-color: #f8f9fa;
    border: 1px solid #e0e0e0;
    color: #4e73df;
    padding: 0.5rem 1rem;
    border-radius: 4px;
    transition: all 0.3s ease;
    font-weight: 600;
    cursor: pointer;
}

.btn-beneficiaire:hover {
    background-color: #4e73df;
    color: white;
    border-color: #4e73df;
}

.beneficiaire-form-card {
    border-radius: 8px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
    margin: 1rem 0;
    border: 1px solid #e0e0e0;
    overflow: hidden;
}

.beneficiaire-form-header {
    background: linear-gradient(135deg, #4e73df, #3a57c2);
    color: white;
    padding: 1rem;
}

.beneficiaire-form-body {
    padding: 1.5rem;
    background-color: #f8f9fa;
}

.btn-close-form {
    background: transparent;
    border: none;
    color: white;
    cursor: pointer;
    font-size: 1.2rem;
    transition: transform 0.2s;
}

.btn-close-form:hover {
    transform: scale(1.2);
}

.btn-save-beneficiaire {
    display: inline-flex;
    align-items: center;
    background-color: #4e73df;
    color: white;
    border: none;
    padding: 0.6rem 1.2rem;
    border-radius: 4px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s ease;
}

.btn-save-beneficiaire:hover {
    background-color: #3a57c2;
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

.form-control {
    border-radius: 4px;
    border: 1px solid #ddd;
    padding: 0.5rem 0.75rem;
    transition: border-color 0.3s, box-shadow 0.3s;
}

.form-control:focus {
    border-color: #4e73df;
    box-shadow: 0 0 0 0.2rem rgba(78, 115, 223, 0.25);
}

.form-label {
    font-size: 0.9rem;
    font-weight: 600;
    margin-bottom: 0.5rem;
    color: #333;
}

.invalid-feedback {
    display: block;
    font-size: 0.8rem;
    color: #e74a3b;
}

/* Styles pour les méthodes de paiement */
.payment-methods-container {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
    gap: 1.5rem;
    margin-bottom: 2rem;
}

.payment-method-item {
    border: 2px solid #e0e0e0;
    border-radius: 8px;
    padding: 0;
    cursor: pointer;
    transition: all 0.3s ease;
    overflow: hidden;
}

.payment-method-selected {
    border-color: #4e73df;
    box-shadow: 0 0 15px rgba(78, 115, 223, 0.3);
}

.payment-method-content {
    display: flex;
    align-items: center;
    padding: 1.25rem;
}

.payment-method-image-container {
    width: 60px;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-right: 1rem;
}

.payment-method-image {
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
}

.payment-method-info {
    flex-grow: 1;
    margin-right: 1rem;
}

.payment-method-title {
    font-weight: 600;
    font-size: 1rem;
    color: #333;
    margin-bottom: 0.25rem;
}

.payment-method-description {
    font-size: 0.85rem;
    color: #6c757d;
}

.payment-method-radio {
    display: flex;
    align-items: center;
}

.custom-radio-container {
    display: inline-block;
    position: relative;
}

.custom-radio-input {
    position: absolute;
    opacity: 0;
    cursor: pointer;
}

.custom-radio-label {
    position: relative;
    cursor: pointer;
    margin: 0;
}

.custom-radio-button {
    display: inline-block;
    position: relative;
    height: 20px;
    width: 20px;
    background-color: #fff;
    border: 2px solid #ccc;
    border-radius: 50%;
}

.custom-radio-input:checked ~ .custom-radio-label .custom-radio-button {
    border-color: #4e73df;
}

.custom-radio-input:checked ~ .custom-radio-label .custom-radio-button::after {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background-color: #4e73df;
}

/* Style pour le bouton de paiement */
.btn-primary {
    background-color: #4e73df;
    border-color: #4e73df;
    transition: all 0.3s ease;
}

.btn-primary:hover {
    background-color: #3a57c2;
    border-color: #3a57c2;
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

.form-check-input:checked {
    background-color: #4e73df;
    border-color: #4e73df;
}

/* Styles pour les conditions générales et le bouton de paiement */
.terms-container {
    margin: 1.5rem 0;
}

.custom-checkbox {
    display: flex;
    align-items: flex-start;
    position: relative;
    padding-left: 30px;
    cursor: pointer;
    user-select: none;
}

.custom-checkbox-input {
    position: absolute;
    opacity: 0;
    cursor: pointer;
    height: 0;
    width: 0;
}

.custom-checkbox-box {
    position: absolute;
    top: 2px;
    left: 0;
    height: 20px;
    width: 20px;
    background-color: #fff;
    border: 2px solid #ccc;
    border-radius: 4px;
    transition: all 0.2s ease;
}

.custom-checkbox-input:checked ~ .custom-checkbox-box {
    background-color: #4e73df;
    border-color: #4e73df;
}

.custom-checkbox-input:checked ~ .custom-checkbox-box:after {
    content: '';
    position: absolute;
    left: 6px;
    top: 2px;
    width: 5px;
    height: 10px;
    border: solid white;
    border-width: 0 2px 2px 0;
    transform: rotate(45deg);
}

.custom-checkbox-label {
    color: #333;
    font-size: 0.95rem;
}

.terms-link {
    color: #4e73df;
    text-decoration: none;
    font-weight: 600;
    transition: color 0.2s;
}

.terms-link:hover {
    color: #3a57c2;
    text-decoration: underline;
}

.checkout-summary-container {
    background-color: #f8f9fa;
    border-radius: 8px;
    padding: 1.5rem;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
    display: flex;
    flex-direction: column;
    align-items: flex-end;
}

.checkout-total {
    width: 100%;
    display: flex;
    justify-content: flex-end;
    align-items: center;
    margin-bottom: 1.5rem;
}

.checkout-total-label {
    font-size: 1.1rem;
    color: #333;
    margin-right: 1rem;
}

.checkout-total-amount {
    font-size: 1.8rem;
    font-weight: 700;
    color: #4e73df;
}

.checkout-total-point-amount {
    font-size: 1rem;
    font-weight: 700;
    color: #4e73df;
}

.checkout-total-discount-amount {
    font-size: 1rem;
    font-weight: 700;
    color: #28a745;
}

.checkout-total-discount-price {
    font-size: 1.2rem;
    font-weight: 600;
    color: #999;
    text-decoration: line-through;
    margin-right: 1rem;
}

.btn-checkout {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: linear-gradient(135deg, #4e73df, #3a57c2);
    color: white;
    font-weight: 600;
    padding: 1rem 2rem;
    border: none;
    border-radius: 8px;
    cursor: pointer;
    transition: all 0.3s ease;
    font-size: 1.1rem;
    box-shadow: 0 4px 12px rgba(78, 115, 223, 0.3);
}

.btn-checkout:hover:not(:disabled) {
    background: linear-gradient(135deg, #3a57c2, #2e479c);
    transform: translateY(-3px);
    box-shadow: 0 6px 15px rgba(78, 115, 223, 0.4);
}

.btn-checkout:disabled {
    background: linear-gradient(135deg, #6c757d, #5a6268) !important;
    color: #fff !important;
    cursor: not-allowed !important;
    opacity: 0.6 !important;
    transform: none !important;
    box-shadow: none !important;
}

/* Styles pour le formulaire d'adresse */
.address-button-container {
    margin: 1.5rem 0;
}

.btn-address {
    display: inline-flex;
    align-items: center;
    background-color: #f8f9fa;
    border: 1px solid #e0e0e0;
    color: #4e73df;
    padding: 0.8rem 1.5rem;
    border-radius: 6px;
    transition: all 0.3s ease;
    font-weight: 600;
    cursor: pointer;
    font-size: 1rem;
}

.btn-address:hover {
    background-color: #4e73df;
    color: white;
    border-color: #4e73df;
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(78, 115, 223, 0.2);
}

.address-form-card {
    border-radius: 8px;
    box-shadow: 0 6px 15px rgba(0, 0, 0, 0.08);
    margin: 1rem 0 2rem;
    border: 1px solid #e0e0e0;
    overflow: hidden;
    background-color: white;
}

.address-form-header {
    background: linear-gradient(135deg, #4e73df, #3a57c2);
    color: white;
    padding: 1rem 1.5rem;
}

.address-form-body {
    padding: 2rem;
    background-color: #f8f9fa;
}

.btn-save-address {
    display: inline-flex;
    align-items: center;
    background: linear-gradient(135deg, #4e73df, #3a57c2);
    color: white;
    border: none;
    padding: 0.8rem 1.8rem;
    border-radius: 6px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s ease;
    font-size: 1rem;
    box-shadow: 0 4px 8px rgba(78, 115, 223, 0.25);
    margin-top: 1rem;
}

.btn-save-address:hover {
    transform: translateY(-3px);
    box-shadow: 0 6px 12px rgba(78, 115, 223, 0.4);
    background: linear-gradient(135deg, #3a57c2, #2e479c);
}

.form-label.required::after {
    content: "*";
    color: #e74a3b;
    margin-left: 3px;
}

.form-select {
    border-radius: 4px;
    border: 1px solid #ddd;
    padding: 0.5rem 0.75rem;
    transition: border-color 0.3s, box-shadow 0.3s;
    height: 40px;
}

.form-select:focus {
    border-color: #4e73df;
    box-shadow: 0 0 0 0.2rem rgba(78, 115, 223, 0.25);
}

.form-select:disabled {
    background-color: #f0f0f0;
    cursor: not-allowed;
}

/* Styles pour les adresses existantes */
.existing-addresses-container {
    margin-bottom: 2rem;
}

.address-selection-header {
    margin-bottom: 1.5rem;
    border-bottom: 1px solid #e0e0e0;
    padding-bottom: 1rem;
}

.addresses-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
    gap: 1.5rem;
    margin-bottom: 2rem;
}

.address-card {
    border: 1px solid #e0e0e0;
    border-radius: 8px;
    overflow: hidden;
    transition: all 0.3s ease;
    cursor: pointer;
    background-color: white;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.address-card:hover {
    transform: translateY(-3px);
    box-shadow: 0 6px 12px rgba(0, 0, 0, 0.08);
    border-color: #bbd0ff;
}

.address-selected {
    border: 2px solid #4e73df;
    background-color: #f5f8ff;
}

.address-card-header {
    padding: 1rem;
    background-color: #f8f9fa;
    border-bottom: 1px solid #e0e0e0;
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.selection-indicator {
    display: flex;
    align-items: center;
}

.address-name {
    font-weight: 600;
    margin-left: 0.75rem;
}

.default-badge {
    background-color: #4e73df;
    color: white;
    padding: 0.25rem 0.75rem;
    border-radius: 20px;
    font-size: 0.8rem;
    font-weight: 600;
}

.address-card-body {
    padding: 1.25rem;
}

.address-details, .contact-details {
    margin-bottom: 1rem;
}

.address-details p, .contact-details p {
    margin-bottom: 0.5rem;
    display: flex;
    align-items: center;
}

.company-name {
    font-weight: 600;
    color: #555;
}

.address-line {
    margin-top: 0.5rem;
}

.city-postal, .country-state {
    color: #666;
}

.phone, .email {
    font-size: 0.95rem;
    color: #555;
}

.actions-container {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 1rem;
    margin-top: 1rem;
}

.btn-use-address {
    display: inline-flex;
    align-items: center;
    background: linear-gradient(135deg, #4e73df, #3a57c2);
    color: white;
    border: none;
    padding: 0.8rem 1.8rem;
    border-radius: 6px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s ease;
    font-size: 1rem;
    box-shadow: 0 4px 8px rgba(78, 115, 223, 0.25);
}

.btn-use-address:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 12px rgba(78, 115, 223, 0.4);
    background: linear-gradient(135deg, #3a57c2, #2e479c);
}

.btn-use-address:disabled {
    opacity: 0.7;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
}

.or-separator {
    font-weight: 600;
    color: #666;
    margin: 0 0.5rem;
}

.custom-radio {
    position: relative;
    display: inline-block;
    width: 20px;
    height: 20px;
    border: 2px solid #4e73df;
    border-radius: 50%;
}

.custom-radio::after {
    content: "";
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background-color: #4e73df;
    opacity: 0;
    transition: opacity 0.2s;
}

input[type="radio"]:checked + .custom-radio::after {
    opacity: 1;
}

.visually-hidden {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
}

.btn-back {
    display: inline-flex;
    align-items: center;
    background: transparent;
    color: #4e73df;
    border: none;
    padding: 0.5rem;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s ease;
    font-size: 0.9rem;
}

.btn-back:hover {
    color: #3a57c2;
    text-decoration: underline;
}

.back-to-addresses {
    padding: 0.75rem 1.5rem;
    background-color: #f0f4ff;
    border-bottom: 1px solid #e0e6fa;
}

/* Styles pour les options supplémentaires */
.options-container {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(min(100%, 300px), 1fr));
    gap: 1.5rem;
    margin-bottom: 2rem;
}

.option-item {
    border: 2px solid #e0e0e0;
    border-radius: 8px;
    padding: 0;
    cursor: pointer;
    transition: all 0.3s ease;
    overflow: hidden;
    background-color: white;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.option-item:hover {
    transform: translateY(-3px);
    box-shadow: 0 6px 12px rgba(0, 0, 0, 0.08);
    border-color: #bbd0ff;
}

.option-selected {
    border-color: #4e73df;
    box-shadow: 0 0 15px rgba(78, 115, 223, 0.3);
    background-color: #f5f8ff;
}

.option-content {
    display: flex;
    align-items: center;
    padding: 1.25rem;
}

.option-image-container {
    width: 100px;
    height: 70px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-right: 1rem;
    border-radius: 6px;
    overflow: hidden;
    background-color: #f8f9fa;
}

.option-image {
    max-width: 100%;
    max-height: 100%;
    object-fit: cover;
}

.option-info {
    min-width: 0;
    overflow-wrap: anywhere;
    flex-grow: 1;
    margin-right: 1rem;
}

.option-title {
    font-weight: 600;
    font-size: 1.1rem;
    color: #333;
    margin-bottom: 0.25rem;
}

.option-description {
    font-size: 0.85rem;
    color: #6c757d;
    margin-bottom: 0.5rem;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
}

.option-price {
    font-weight: 700;
    font-size: 1.1rem;
    color: #4e73df;
}

.option-checkbox {
    flex: 0 0 24px;
    display: flex;
    align-items: center;
}

.option-selected .option-checkbox .custom-checkbox-button {
    border-color: #4e73df;
}

.option-selected .option-checkbox .custom-checkbox-button::after {
    opacity: 1;
}

/* Style pour le bouton d'ajout d'options */
.btn-add-options {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: linear-gradient(135deg, #28a745, #20c997);
    color: white;
    font-weight: 600;
    padding: 1rem 2rem;
    border: none;
    border-radius: 8px;
    cursor: pointer;
    transition: all 0.3s ease;
    font-size: 1.1rem;
    box-shadow: 0 4px 12px rgba(40, 167, 69, 0.3);
    text-decoration: none;
}

.btn-add-options:hover {
    background: linear-gradient(135deg, #218838, #1e7e34);
    transform: translateY(-3px);
    box-shadow: 0 6px 15px rgba(40, 167, 69, 0.4);
    color: white;
}

.btn-add-options:active {
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(40, 167, 69, 0.4);
}

/* Styles pour la section code promo */
.promo-code-section {
    margin-bottom: 2rem;
    background-color: #f8f9fa;
    border-radius: 8px;
    padding: 1.5rem;
    box-shadow: 0 2px 5px rgba(0, 0, 0, 0.05);
}

.promo-code-container {
    position: relative;
}

.promo-code-form {
    position: relative;
    transition: all 0.3s ease;
}

.promo-code-form.is-loading {
    opacity: 0.7;
}

.input-group {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
    border-radius: 6px;
    overflow: hidden;
}

.input-group-text {
    background-color: #f0f0f0;
    border-color: #e0e0e0;
    color: #4e73df;
}

.btn-apply-code {
    background: linear-gradient(135deg, #4e73df, #3a57c2);
    color: white;
    border: none;
    font-weight: 600;
    padding: 0 1.5rem;
    min-width: 120px;
    transition: all 0.3s ease;
}

.btn-apply-code:hover:not(:disabled) {
    background: linear-gradient(135deg, #3a57c2, #2e479c);
    box-shadow: 0 4px 8px rgba(78, 115, 223, 0.3);
}

.btn-apply-code:disabled {
    background: #a0a0a0;
    cursor: not-allowed;
}

.promo-code-message {
    display: flex;
    align-items: center;
    font-size: 0.9rem;
    padding: 0.5rem 0;
    transition: all 0.3s ease;
}

.promo-code-message.is-success {
    color: #28a745;
}

.promo-code-message.is-error {
    color: #dc3545;
}

.applied-promo-code {
    display: flex;
    align-items: center;
    justify-content: space-between;
    background-color: #e8f4ff;
    border: 1px dashed #4e73df;
    border-radius: 6px;
    padding: 0.75rem 1rem;
    transition: all 0.3s ease;
}

.applied-code-details {
    display: flex;
    align-items: center;
    gap: 1rem;
}

.code-badge {
    background-color: #4e73df;
    color: white;
    padding: 0.3rem 0.75rem;
    border-radius: 20px;
    font-size: 0.9rem;
    font-weight: 600;
    display: flex;
    align-items: center;
}

.discount-amount {
    font-weight: 700;
    color: #28a745;
    font-size: 1.1rem;
}

.btn-remove-code {
    background: none;
    border: none;
    color: #6c757d;
    cursor: pointer;
    padding: 0.25rem;
    border-radius: 50%;
    width: 28px;
    height: 28px;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s ease;
}

.btn-remove-code:hover {
    background-color: #f0f0f0;
    color: #dc3545;
}

/* Styles pour le bloc d'envoi d'email des cartes cadeaux */
.gift-card-email-card {
    border-left: 4px solid #28a745 !important;
}

.gift-card-email-card .card-header h5 {
    color: #28a745;
}

.gift-card-email-card fieldset {
    border: none;
    padding: 0;
    margin: 0;
}

.gift-card-email-card legend {
    font-size: 1rem;
    margin-bottom: 0.75rem;
    width: auto;
}

.gift-card-email-card .form-check-input:checked {
    background-color: #28a745;
    border-color: #28a745;
}

/* Styles pour la section d'authentification */
.user-auth-section {
    margin: 2rem 0;
}

.auth-card {
    background: linear-gradient(135deg, #f8f9fa, #ffffff);
    border: 1px solid #e0e0e0;
    border-radius: 12px;
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.08);
    overflow: hidden;
    transition: all 0.3s ease;
}

.auth-card:hover {
    transform: translateY(-3px);
    box-shadow: 0 8px 25px rgba(78, 115, 223, 0.15);
}

.auth-card-content {
    display: flex;
    padding: 2rem;
    align-items: center;
    border-bottom: 1px solid #eaeaea;
}

.auth-icon {
    font-size: 3rem;
    color: #4e73df;
    margin-right: 1.5rem;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 80px;
    height: 80px;
    background-color: rgba(78, 115, 223, 0.1);
    border-radius: 50%;
    flex-shrink: 0;
}

.auth-text {
    flex-grow: 1;
}

.auth-title {
    font-size: 1.4rem;
    font-weight: 600;
    color: #333;
    margin-bottom: 0.5rem;
}

.auth-description {
    color: #666;
    margin-bottom: 0;
    line-height: 1.5;
}

.auth-actions {
    display: flex;
    padding: 1.5rem;
    gap: 1rem;
    background-color: #f8f9fa;
}

.btn-auth {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0.8rem 1.5rem;
    border-radius: 6px;
    font-weight: 600;
    text-decoration: none;
    transition: all 0.3s ease;
    flex: 1;
}

.btn-login {
    background-color: #4e73df;
    color: white;
    border: 1px solid #4e73df;
}

.btn-login:hover {
    background-color: #3a57c2;
    color: white;
    transform: translateY(-2px);
    box-shadow: 0 4px 10px rgba(78, 115, 223, 0.3);
}

.btn-register {
    background-color: white;
    color: #4e73df;
    border: 1px solid #4e73df;
}

.btn-register:hover {
    background-color: #f0f4ff;
    transform: translateY(-2px);
    box-shadow: 0 4px 10px rgba(78, 115, 223, 0.15);
}

/* Animation pour l'icône de chargement */
.spin {
    animation: spin 1s linear infinite;
}

@keyframes spin {
    from {
        transform: rotate(0deg);
    }
    to {
        transform: rotate(360deg);
    }
}
</style>
